import algoliasearch from 'algoliasearch';
import { HTTP } from './http';

// Default prefix for object IDs. This is the prefix used currently in Algolia and it's our convention.
const DEFAULT_OBJECT_PREFIX = 'WoS_';

// Default API endpoint template for records if using API driver. Not used for Algolia driver.
const DEFAULT_API_RECORDS_ENDPOINT = 'search/indexes/{index}/records';

// Checks if a provider implements required methods. This should be manually implemented by custom adapters to be compatible with the mixins.
const hasProviderMethods = (provider) => {
    return provider && typeof provider.search === 'function' && typeof provider.getObject === 'function' && typeof provider.getObjects === 'function';
};

// Resolves object prefix from options or uses default, so we can override it if needed.
const resolveObjectPrefix = (searchOptions) => {
    return (searchOptions && searchOptions.objectPrefix) || DEFAULT_OBJECT_PREFIX;
};

// Formats a locationId into an objectId, which should contain the prefix + the ID.
const toObjectId = (locationId, objectPrefix) => {
    if (locationId === null || typeof locationId === 'undefined') {
        return null;
    }

    const normalisedId = String(locationId);

    if (normalisedId.indexOf('_') > -1) {
        return normalisedId;
    }

    return `${objectPrefix}${normalisedId}`;
};

// Picks an array from various possible response shapes, so we can get the data depending on the driver used (Algolia or API).
const pickArray = (value) => {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value;
    }

    if (Array.isArray(value.hits)) {
        return value.hits;
    }

    if (Array.isArray(value.results)) {
        return value.results;
    }

    if (Array.isArray(value.records)) {
        return value.records;
    }

    if (value.data) {
        return pickArray(value.data);
    }

    return [];
};

const isNotFoundError = (error) => {
    return error && error.response && error.response.status === 404;
};

const normaliseObjectId = (objectId) => {
    if (objectId === null || typeof objectId === 'undefined') {
        return '';
    }

    return String(objectId);
};

// Returns possible candidates for matching an objectId
const getObjectIdCandidates = (objectId) => {
    const normalised = normaliseObjectId(objectId);

    if (!normalised) {
        return [];
    }

    const candidates = [normalised];
    const split = normalised.split('_');

    if (split.length > 1) {
        candidates.push(split[split.length - 1]);
    }

    // Remove duplicates
    return candidates.filter((value, index, all) => all.indexOf(value) === index);
};

// Builds a filter string for searching by objectIds
const buildObjectFilters = (objectIds) => {
    const clauses = [];

    objectIds.forEach((objectId) => {
        getObjectIdCandidates(objectId).forEach((candidate) => {
            clauses.push(`objectID:${candidate}`);
            clauses.push(`id:${candidate}`);
        });
    });

    const uniqueClauses = clauses.filter((value, index, all) => all.indexOf(value) === index);

    if (!uniqueClauses.length) {
        return '';
    }

    return `(${uniqueClauses.join(' OR ')})`;
};

// Converts query and options to API params
const toApiParams = (query, options = {}) => {
    const params = {};

    if (typeof query !== 'undefined' && query !== null && String(query).trim() !== '') {
        params.q = query;
    }

    if (typeof options.q !== 'undefined') {
        params.q = options.q;
    } else if (typeof options.query !== 'undefined') {
        params.q = options.query;
    }

    if (typeof options.per_page !== 'undefined') {
        params.per_page = options.per_page;
    } else if (typeof options.hitsPerPage !== 'undefined') {
        params.per_page = options.hitsPerPage;
    }

    if (typeof options.page !== 'undefined') {
        params.page = options.page;
    }

    if (typeof options.filters !== 'undefined') {
        params.filters = options.filters;
    }

    if (typeof options.around_lat_lng !== 'undefined') {
        params.around_lat_lng = options.around_lat_lng;
    } else if (typeof options.aroundLatLng !== 'undefined') {
        params.around_lat_lng = options.aroundLatLng;
    }

    if (typeof options.around_radius !== 'undefined') {
        params.around_radius = options.around_radius;
    } else if (typeof options.aroundRadius !== 'undefined') {
        params.around_radius = options.aroundRadius;
    }

    if (typeof options.attributes_to_retrieve !== 'undefined') {
        params.attributes_to_retrieve = options.attributes_to_retrieve;
    } else if (Array.isArray(options.attributesToRetrieve)) {
        params.attributes_to_retrieve = options.attributesToRetrieve.join(',');
    } else if (typeof options.attributesToRetrieve === 'string') {
        params.attributes_to_retrieve = options.attributesToRetrieve;
    }

    return params;
};

// Extracts the record's id (objectID or id)
const extractRecordId = (record) => {
    if (!record) {
        return '';
    }

    if (typeof record.objectID !== 'undefined' && record.objectID !== null) {
        return String(record.objectID);
    }

    if (typeof record.id !== 'undefined' && record.id !== null) {
        return String(record.id);
    }

    return '';
};

// Sorts records to match the order of requested objectIds
const sortByRequestedObjectIds = (records, objectIds) => {
    if (!Array.isArray(records) || !Array.isArray(objectIds)) {
        return [];
    }

    const requestedOrder = objectIds.map((objectId) => getObjectIdCandidates(objectId));

    const scoreRecord = (recordId) => {
        for (let i = 0; i < requestedOrder.length; i += 1) {
            if (requestedOrder[i].indexOf(recordId) > -1) {
                return i;
            }
        }

        return Number.MAX_SAFE_INTEGER;
    };

    return records.slice(0).sort((left, right) => scoreRecord(extractRecordId(left)) - scoreRecord(extractRecordId(right)));
};

// Creates an Algolia-based location search adapter. This is the default driver if no custom adapter or API driver is specified.
const createAlgoliaLocationSearchAdapter = (searchOptions) => {
    if (!searchOptions || !searchOptions.appId || !searchOptions.apiKey) {
        throw new Error('Missing Algolia location search config (appId/apiKey).');
    }

    const indexPrefix = searchOptions.prefix || '';
    const indexName = `${indexPrefix}locations`;
    const client = algoliasearch(searchOptions.appId, searchOptions.apiKey);
    const index = client.initIndex(indexName);

    return {
        // Search locations using Algolia
        search(query, options = {}) {
            return new Promise((resolve, reject) => {
                index.search(query, options, (err, content) => {
                    if (err) {
                        reject(err);
                        return;
                    }

                    resolve(content && content.hits ? content.hits : []);
                });
            });
        },
        // Get a single location object by id
        getObject(objectId) {
            return new Promise((resolve, reject) => {
                index.getObject(objectId, (err, content) => {
                    if (err) {
                        reject(err);
                        return;
                    }

                    resolve(content);
                });
            });
        },
        // Get multiple location objects by ids
        getObjects(objectIds) {
            return new Promise((resolve, reject) => {
                index.getObjects(objectIds, (err, content) => {
                    if (err) {
                        reject(err);
                        return;
                    }

                    resolve(content && content.results ? content.results : []);
                });
            });
        },
    };
};

// Builds the base URL for API records endpoint
const buildApiRecordsBaseUrl = (searchOptions, indexName) => {
    const endpointTemplate = searchOptions.locationSearchEndpoint || searchOptions.searchEndpoint || DEFAULT_API_RECORDS_ENDPOINT;

    return endpointTemplate.replace('{index}', encodeURIComponent(indexName));
};

// Creates an API-based location search adapter
const createApiLocationSearchAdapter = (searchOptions) => {
    const indexPrefix = searchOptions.prefix || '';
    const indexName = `${indexPrefix}locations`;
    const recordsBaseUrl = buildApiRecordsBaseUrl(searchOptions, indexName);

    return {
        // Search locations using API
        search(query, options = {}) {
            const params = toApiParams(query, options);

            return HTTP.post(recordsBaseUrl, params).then((response) => pickArray(response.data));
        },
        // Get a single location object by id using API
        getObject(objectId) {
            if (!objectId) {
                return Promise.resolve(null);
            }

            const params = toApiParams('', {
                per_page: 1,
                filters: buildObjectFilters([objectId]),
            });

            return HTTP.post(recordsBaseUrl, params).then((response) => {
                const records = pickArray(response.data);

                if (records.length > 0) {
                    return records[0];
                }

                return null;
            });
        },
        // Get multiple location objects by ids using API
        getObjects(objectIds) {
            if (!Array.isArray(objectIds) || objectIds.length < 1) {
                return Promise.resolve([]);
            }

            const params = toApiParams('', {
                per_page: Math.max(objectIds.length, 1),
                filters: buildObjectFilters(objectIds),
            });

            return HTTP.post(recordsBaseUrl, params)
                .then((response) => pickArray(response.data))
                .then((records) => {
                    if (!records.length) {
                        // Fallback: fetch individually if not found in batch
                        return Promise.all(objectIds.map((objectId) => this.getObject(objectId))).then((results) => results.filter((result) => !!result));
                    }

                    return sortByRequestedObjectIds(records, objectIds);
                })
                .catch((error) => {
                    if (!isNotFoundError(error)) {
                        return Promise.reject(error);
                    }

                    // Fallback for 404: fetch individually
                    return Promise.all(objectIds.map((objectId) => this.getObject(objectId))).then((results) => results.filter((result) => !!result));
                });
        },
    };
};

// Determines which search driver to use (algolia or api). Defaults to algolia if not specified.
const resolveLocationSearchDriver = (searchOptions) => {
    if (!searchOptions) {
        return 'algolia';
    }

    return searchOptions.locationSearchDriver || searchOptions.searchDriver || searchOptions.driver || 'algolia';
};

// Factory to create a location search adapter (custom, API, or Algolia). Defaults to Algolia if no custom adapter or API driver is specified.
export const createLocationSearchAdapter = (searchOptions) => {
    const customAdapter = searchOptions && (searchOptions.locationSearchAdapter || searchOptions.searchAdapter);

    if (typeof customAdapter === 'function') {
        const adapter = customAdapter(searchOptions);

        if (hasProviderMethods(adapter)) {
            return adapter;
        }
    } else if (hasProviderMethods(customAdapter)) {
        return customAdapter;
    }

    if (resolveLocationSearchDriver(searchOptions) === 'api') {
        return createApiLocationSearchAdapter(searchOptions || {});
    }

    return createAlgoliaLocationSearchAdapter(searchOptions);
};

// Utility to get a location objectId with correct prefix
export const getLocationObjectId = (locationId, searchOptions) => {
    return toObjectId(locationId, resolveObjectPrefix(searchOptions));
};
